import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Activity, DollarSign } from 'lucide-react';
import { PriceBucket } from '../../types';

interface PerformancePerFasciaChartProps {
  data: PriceBucket[];
}

export const PerformancePerFasciaChart: React.FC<PerformancePerFasciaChartProps> = ({ data }) => {
  const [selectedMetric, setSelectedMetric] = useState<'successRate' | 'avgSpike' | 'pmhBreakRate' | 'avgVolumeRatio'>('successRate');

  const metrics = [
    { id: 'successRate' as const, label: 'Success Rate', icon: TrendingUp, color: 'green' },
    { id: 'avgSpike' as const, label: 'Avg Spike %', icon: Activity, color: 'blue' },
    { id: 'pmhBreakRate' as const, label: 'PMH Break %', icon: TrendingDown, color: 'yellow' },
    { id: 'avgVolumeRatio' as const, label: 'Volume Ratio', icon: DollarSign, color: 'purple' }
  ];

  const maxValue = Math.max(...data.map(d => d[selectedMetric]));

  const getColorClass = (metric: string) => {
    switch (metric) {
      case 'green': return 'bg-green-500';
      case 'blue': return 'bg-blue-500';
      case 'yellow': return 'bg-yellow-500';
      case 'purple': return 'bg-purple-500';
      default: return 'bg-blue-500';
    }
  };

  const getTextColorClass = (metric: string) => {
    switch (metric) {
      case 'green': return 'text-green-400';
      case 'blue': return 'text-blue-400';
      case 'yellow': return 'text-yellow-400';
      case 'purple': return 'text-purple-400';
      default: return 'text-blue-400';
    }
  };

  return (
    <div className="premium-chart-card">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h4 className="premium-chart-title">Performance per Fascia di Prezzo</h4>
          <p className="premium-chart-subtitle">Confronto metriche tra diverse fasce di prezzo</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {metrics.map(metric => {
            const MetricIcon = metric.icon;
            const isSelected = selectedMetric === metric.id;
            return (
              <button
                key={metric.id}
                onClick={() => setSelectedMetric(metric.id)}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  isSelected
                    ? `${getColorClass(metric.color)}/20 ${getTextColorClass(metric.color)} border border-${metric.color}-500/50`
                    : 'bg-gray-700/50 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <div className="flex items-center space-x-2">
                  <MetricIcon className="h-3 w-3" />
                  <span>{metric.label}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-3">
        {data.map((bucket, index) => {
          const value = bucket[selectedMetric];
          const widthPercent = maxValue > 0 ? (value / maxValue) * 100 : 0;
          const selectedMetricConfig = metrics.find(m => m.id === selectedMetric)!;

          return (
            <div key={index} className="group">
              <div className="flex items-center justify-between mb-1">
                <span className="text-sm font-medium text-white">{bucket.label}</span>
                <div className="flex items-center space-x-3">
                  <span className="text-xs text-gray-400">
                    {bucket.count} gap
                  </span>
                  <span className={`text-sm font-bold ${getTextColorClass(selectedMetricConfig.color)}`}>
                    {value.toFixed(1)}{selectedMetric === 'avgVolumeRatio' ? 'x' : '%'}
                  </span>
                </div>
              </div>

              <div className="relative h-8 bg-gray-800 rounded-lg overflow-hidden">
                <div
                  className={`absolute inset-y-0 left-0 ${getColorClass(selectedMetricConfig.color)}/80 transition-all duration-500 group-hover:${getColorClass(selectedMetricConfig.color)}`}
                  style={{ width: `${widthPercent}%` }}
                />

                <div className="absolute inset-0 flex items-center justify-between px-3">
                  <span className="text-xs text-white/80 font-medium">
                    Red: {bucket.redCloseRate.toFixed(1)}%
                  </span>
                  {bucket.count > 0 && (
                    <span className="text-xs text-white/60">
                      Avg Vol: {(bucket.avgVolume / 1e6).toFixed(1)}M
                    </span>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 pt-4 border-t border-gray-700">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {metrics.map(metric => {
            const total = data.reduce((sum, d) => sum + d[metric.id], 0);
            const avg = data.length > 0 ? total / data.length : 0;
            const MetricIcon = metric.icon;

            return (
              <div key={metric.id} className="text-center">
                <div className="flex items-center justify-center space-x-1 mb-1">
                  <MetricIcon className={`h-4 w-4 ${getTextColorClass(metric.color)}`} />
                  <span className="text-xs text-gray-400">{metric.label}</span>
                </div>
                <p className={`text-lg font-bold ${getTextColorClass(metric.color)}`}>
                  {avg.toFixed(1)}{metric.id === 'avgVolumeRatio' ? 'x' : '%'}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
